import type {
  AppearanceModule,
  AppearanceStyle,
  AppearanceStyleSet,
} from '@setplex/tria-api'
import type { Options } from '~/core/flags'
import { COLOR } from '@setplex/arcana'

export const formatColorConfiguration = (style: AppearanceStyle) => {
  if (style.type === 'COLOR') {
    return style.color
  }

  if (style.type === 'GRADIENT') {
    const { angle, list } = style.configuration

    const colors = list.map(
      (colorConfig) => `${colorConfig.color} ${colorConfig.percentage}%`
    )
    return `linear-gradient(${angle}deg, ${colors.join(', ')})`
  }

  // in case of UNSET | IMAGE | undefined
  return null
}

export const formatAppTheme = (
  theme: AppearanceStyleSet
): React.CSSProperties => {
  const primaryColor = formatColorConfiguration(theme.mainStyle)
  const tertiaryColor = formatColorConfiguration(theme.additionalStyle)
  const backgroundColor = formatColorConfiguration(theme.backgroundStyle)
  const secondaryColor = formatColorConfiguration(theme.secondaryStyle)

  return {
    [COLOR.ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.BUTTON_ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.CATEGORY_CARD_ACCENT_PRIMARY_GRADIENT]: primaryColor,
    [COLOR.ACCENT_TERTIARY]: tertiaryColor,
    [COLOR.BACKGROUND_DARK_APP]: backgroundColor,
    [COLOR.ACCENT_SECONDARY]: secondaryColor,
    [COLOR.PROGRESS_BAR_ACCENT_SECONDARY]: secondaryColor,
  } as React.CSSProperties
}

// This values predefined by doc
// https://setplexapps.atlassian.net/wiki/spaces/NORA/pages/3808034876/Backward+Compatibility+and+Migration
export const STATIC_IDENTIFIERS = {
  HOME: 'home',
  TV: 'tv_channels',
  VOD: 'movie',
  SHOW: 'tv_shows',
  CATCHUP: 'catchups',
  EPG: 'epg',
  EVENT: 'events',
  SEARCH: 'search',
  MY_LIST: 'my_list',
  SETTINGS: 'settings',
} as const

// modules that used when /appearance api not available
// fallbackFlag - optional property from /auth - fallback visibility
// https://setplexapps.atlassian.net/wiki/spaces/NORA/pages/3808034876/Backward+Compatibility+and+Migration
export const STATIC_MODULES = [
  {
    iconName: 'home',
    identifier: 'home',
    localizedTitleKey: 'home',
    title: 'Home',
    type: 'static',
  },
  {
    fallbackFlag: 'tvEnabled',
    iconName: 'tv',
    identifier: 'tv_channels',
    localizedTitleKey: 'live_navbar',
    title: 'Live',
    type: 'static',
  },
  {
    fallbackFlag: 'vodEnabled',
    iconName: 'discovery',
    identifier: 'movie',
    localizedTitleKey: 'movies',
    title: 'Movies',
    type: 'static',
  },
  {
    fallbackFlag: 'tvShowEnabled',
    iconName: 'series',
    identifier: 'tv_shows',
    localizedTitleKey: 'series',
    title: 'Series',
    type: 'static',
  },
  {
    fallbackFlag: 'catchupEnabled',
    iconName: 'catchups',
    identifier: 'catchups',
    localizedTitleKey: 'catchup_other',
    title: 'Catchups',
    type: 'static',
  },
  {
    fallbackFlag: 'epgEnabled',
    iconName: 'epg',
    identifier: 'epg',
    localizedTitleKey: 'epg',
    title: 'EPG',
    type: 'static',
  },
  {
    fallbackFlag: 'liveEventEnabled',
    iconName: 'activity',
    identifier: 'events',
    localizedTitleKey: 'event_other',
    title: 'Events',
    type: 'static',
  },
] as const

export function getModulesFallback(options: Options): AppearanceModule[] {
  return STATIC_MODULES.filter((mod) =>
    'fallbackFlag' in mod ? options[mod.fallbackFlag] : true
  )
}
